import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import GenericObjectTable from "./GenericObjectTable";
import GenericStructuredValue from "./GenericStructuredValue";
import RunDataView from "./RunDataView";
import { formatThreshold } from "../utils/decisionFormatters";
import { DEFAULT_RUN_DOMAIN, getDomainProfile } from "../config/domainProfiles";
import {
  mergeExternalViews,
  normalizeAlternatives,
  normalizeEntities,
  normalizeIntake,
  normalizePilotReference,
} from "../utils/decisionUiHelpers";

/** @param {ReturnType<typeof getDomainProfile>} labels */
function isRichLayout(labels) {
  return labels.layoutVariant === "cpWealthRich";
}

/** @param {{ labels: ReturnType<typeof getDomainProfile> }} props */
function ShellRecommendationLoading({ labels }) {
  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Box
        sx={{
          borderRadius: 2,
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 2.5 },
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" color="primary" fontWeight={700}>
          {labels.recommendationHeroLabel}
        </Typography>
        <Skeleton variant="text" width="85%" height={36} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="55%" />
      </Box>
      {[labels.sectionConfidence, labels.sectionDrivers, labels.sectionRisks, labels.sectionProof].map((title) => (
        <SectionCard key={title} title={title}>
          <Stack spacing={1}>
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="text" width="65%" />
            <Skeleton variant="text" width="45%" />
          </Stack>
        </SectionCard>
      ))}
    </Stack>
  );
}

/** @param {{ labels: ReturnType<typeof getDomainProfile>; nav: import("react-router-dom").NavigateFunction }} props */
function ShellRecommendationEmpty({ labels, nav }) {
  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Box
        sx={{
          borderRadius: 2,
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 2.5 },
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" color="primary" fontWeight={700}>
          {labels.recommendationHeroLabel}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {labels.summaryPlaceholder}
        </Typography>
      </Box>
      {[labels.sectionConfidence, labels.sectionDrivers, labels.sectionRisks, labels.sectionProof].map((title) => (
        <SectionCard key={title} title={title}>
          <Typography variant="body2" color="text.secondary">
            {labels.shellRecommendationEmptyLine}
          </Typography>
        </SectionCard>
      ))}
      <Button variant="text" size="small" onClick={() => nav("/dictionaries/import-snowflake")} sx={{ alignSelf: "flex-start" }}>
        {labels.connectDatabaseCta}
      </Button>
    </Stack>
  );
}

/**
 * User upload columns + `percentile` only. Display table column order is upload…, percentile, features…
 * @param {unknown} columns
 * @returns {string[] | null}
 */
function ulpRecommendationColumnProjection(columns) {
  if (!Array.isArray(columns) || columns.length === 0) return null;
  const idx = columns.findIndex((c) => String(c).toLowerCase() === "percentile");
  if (idx >= 0) return columns.slice(0, idx + 1).map((c) => String(c));
  return null;
}

/**
 * @param {{
 *   decisionOutput: Record<string, unknown> | null;
 *   loading: boolean;
 *   runId: string | null;
 *   dataRefreshKey?: number;
 *   domainProfile?: ReturnType<typeof getDomainProfile>;
 * }} props
 */
export default function BusinessRecommendation({
  decisionOutput,
  loading,
  runId,
  dataRefreshKey = 0,
  domainProfile: domainProfileProp,
}) {
  const nav = useNavigate();
  const labels = domainProfileProp ?? getDomainProfile(DEFAULT_RUN_DOMAIN);
  const rich = isRichLayout(labels);

  const drivers =
    decisionOutput != null && typeof decisionOutput === "object" && !Array.isArray(decisionOutput)
      ? decisionOutput.drivers ?? decisionOutput.Drivers
      : null;

  const driversFeatureColumnOrder = useMemo(() => {
    const d = drivers;
    if (d == null || typeof d !== "object" || Array.isArray(d)) return null;
    const raw = d.feature_column_order ?? d.Feature_column_order;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    return raw.map((x) => String(x));
  }, [drivers]);

  if (loading && rich) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={200} gap={2}>
        <CircularProgress size={32} />
        <Typography color="text.secondary">{labels.loadingRecommendation}</Typography>
      </Box>
    );
  }

  if (loading && !rich) {
    return <ShellRecommendationLoading labels={labels} />;
  }

  // typeof null === "object" — require a non-null plain object for the recommendation tree
  if (
    decisionOutput == null ||
    typeof decisionOutput !== "object" ||
    Array.isArray(decisionOutput)
  ) {
    if (!rich) {
      return <ShellRecommendationEmpty labels={labels} nav={nav} />;
    }
    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          p: { xs: 2, md: 4 },
          textAlign: "center",
          bgcolor: "background.default",
        }}
      >
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {labels.emptyRecommendationTitle}
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 480, mx: "auto", mb: 2 }}>
          {labels.emptyRecommendationLead}
          <Typography component="span" fontWeight={600}>
            {labels.emptyRecommendationHighlight}
          </Typography>
          {labels.emptyRecommendationTrail}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
          <Button variant="outlined" size="large" onClick={() => nav("/dictionaries/import-snowflake")}>
            {labels.connectDatabaseCta}
          </Button>
        </Stack>
      </Card>
    );
  }

  const summary =
    typeof decisionOutput.summary === "string"
      ? decisionOutput.summary
      : typeof decisionOutput.Summary === "string"
        ? decisionOutput.Summary
        : "";
  const uncertainty = decisionOutput.uncertainty ?? decisionOutput.Uncertainty;
  const guardrails = decisionOutput.guardrails ?? decisionOutput.Guardrails;
  const evidence = decisionOutput.evidence ?? decisionOutput.Evidence;
  const ulpDisplayRaw = decisionOutput.user_live_prediction_display;
  const ulpDisplay =
    ulpDisplayRaw != null && typeof ulpDisplayRaw === "object" && !Array.isArray(ulpDisplayRaw)
      ? ulpDisplayRaw
      : null;
  const showUlpDisplayTable =
    ulpDisplay != null &&
    typeof ulpDisplay.bq_table === "string" &&
    ulpDisplay.bq_table.trim().length > 0;

  const ulpColumnList =
    ulpDisplay != null && Array.isArray(ulpDisplay.columns) ? ulpDisplay.columns : null;
  const ulpRecommendationProjection = ulpRecommendationColumnProjection(ulpColumnList);

  const hasDriversPayload =
    drivers != null && typeof drivers === "object" && !Array.isArray(drivers);
  const showDriversSection = hasDriversPayload || (showUlpDisplayTable && runId);

  const intake = normalizeIntake(decisionOutput, { hasUlpTable: showUlpDisplayTable });
  const mergedExternalViews = mergeExternalViews(
    labels.externalViews,
    decisionOutput.external_views ?? decisionOutput.externalViews
  );
  const entities = normalizeEntities(decisionOutput.entities, 12);
  const alternatives = normalizeAlternatives(decisionOutput);
  const pilotRef = normalizePilotReference(decisionOutput);
  const dataAnchorHref =
    runId != null && String(runId).length > 0
      ? `/runs/${encodeURIComponent(String(runId))}/notebook#notebook-detailed-data`
      : "#notebook-detailed-data";

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      {intake && intake.mode !== "connected_source" ? (
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
            <Chip
              size="small"
              color="default"
              variant="outlined"
              label={labels.intakeLabelExplicitScenario}
            />
            {intake.detail ? (
              <Typography variant="body2" color="text.secondary">
                {labels.intakeDetailPrefix}
                {intake.detail}
              </Typography>
            ) : null}
          </Stack>
        </Box>
      ) : null}

      <Box
        sx={{
          borderRadius: rich ? 3 : 2,
          px: { xs: 2, md: rich ? 4 : 3 },
          py: { xs: rich ? 3 : 2, md: rich ? 4 : 2.5 },
          ...(rich
            ? {
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? `linear-gradient(135deg, ${theme.palette.primary.dark}33 0%, ${theme.palette.background.paper} 100%)`
                    : `linear-gradient(135deg, ${theme.palette.primary.light}55 0%, ${theme.palette.background.paper} 100%)`,
              }
            : { bgcolor: "background.paper" }),
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="h6"
          component="div"
          color="primary"
          fontWeight={700}
          sx={{ fontSize: rich ? { xs: "1.125rem", sm: "1.25rem" } : "1rem", letterSpacing: rich ? 0.02 : 0 }}
        >
          {labels.recommendationHeroLabel}
        </Typography>
        {summary ? (
          <Typography
            variant={rich ? "h5" : "h6"}
            component="h1"
            fontWeight={700}
            sx={{ mt: 1.5, lineHeight: 1.35, maxWidth: 900 }}
          >
            {summary}
          </Typography>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {labels.summaryPlaceholder}
          </Typography>
        )}

        {showUlpDisplayTable && runId ? (
          <>
            <Box sx={{ width: "100%", minWidth: 0, maxWidth: "100%", mt: { xs: 2.5, md: 3 } }}>
              <RunDataView
                key={`ulp-rec-${runId}`}
                runId={runId}
                hideTableSelector
                refreshKey={dataRefreshKey}
                columnProjection={ulpRecommendationProjection}
              />
            </Box>
            {/* Plain bullets, no tinted panel: these read as the same kind of
                explanatory list as Drivers and Risks below, so they are rendered
                the same way — a bare <ul> whose items inherit body text rather
                than a muted body2 inside a grey box. */}
            <Box component="ul" sx={{ mt: 2, mb: 0, pl: 2.5 }}>
              <li>
                <Typography variant="body1" component="span" sx={{ lineHeight: 1.75 }}>
                  For buying equities, lower percentile number means less return and thus should be avoided.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" component="span" sx={{ lineHeight: 1.75 }}>
                  For selling equities, the sorting is reversed from buying, lower percentile number means more
                  opportunity loss and thus should be avoided.
                </Typography>
              </li>
            </Box>
          </>
        ) : null}
      </Box>

      {alternatives.length > 0 ? (
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" fontWeight={700}>
            {labels.sectionAlternatives}
          </Typography>
          <Stack spacing={1.5}>
            {alternatives.map((alt) => (
              <Card key={alt.key} variant="outlined">
                <CardContent sx={{ py: 1.75, "&:last-child": { pb: 1.75 } }}>
                  {alt.summary ? (
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {alt.summary}
                    </Typography>
                  ) : null}
                  {alt.rationale ? (
                    <Typography variant="body2" color="text.secondary">
                      {alt.rationale}
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Stack>
      ) : null}

      <Stack spacing={2} sx={{ width: "100%" }}>
        {uncertainty != null && typeof uncertainty === "object" && !Array.isArray(uncertainty) ? (
          <SectionCard title={labels.sectionConfidence}>
            <UncertaintySection uncertainty={uncertainty} runId={runId || undefined} />
          </SectionCard>
        ) : null}

        {showDriversSection ? (
          <SectionCard title={labels.sectionDrivers}>
            {hasDriversPayload ? <DriversSection drivers={drivers} runId={runId || undefined} /> : null}
            {showUlpDisplayTable && runId ? (
              <Box sx={{ width: "100%", minWidth: 0, mt: hasDriversPayload ? 2 : 0 }}>
                <RunDataView
                  key={`ulp-drivers-${runId}`}
                  runId={runId}
                  hideTableSelector
                  refreshKey={dataRefreshKey}
                  columnProjection={null}
                  columnOrder={driversFeatureColumnOrder}
                />
              </Box>
            ) : null}
          </SectionCard>
        ) : null}

        {guardrails != null ? (
          <SectionCard title={labels.sectionRisks}>
            <GenericStructuredValue value={guardrails} runId={runId || undefined} />
          </SectionCard>
        ) : null}

        {evidence != null && typeof evidence === "object" && !Array.isArray(evidence) ? (
          <SectionCard title={labels.sectionProof}>
            <EvidenceProofSection evidence={evidence} runId={runId || undefined} />
          </SectionCard>
        ) : null}

        {entities.length > 0 ? (
          <SectionCard title={labels.sectionEntitiesTop}>
            <Stack spacing={1}>
              {entities.map((ent) => (
                <Box
                  key={ent.key}
                  sx={{
                    py: 0.75,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none", pb: 0 },
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Box minWidth={0}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {ent.name}
                      </Typography>
                      {ent.summary ? (
                        <Typography variant="body2" color="text.secondary">
                          {ent.summary}
                        </Typography>
                      ) : null}
                    </Box>
                    {ent.scoreLabel ? (
                      <Chip size="small" label={ent.scoreLabel} variant="outlined" sx={{ flexShrink: 0 }} />
                    ) : null}
                  </Stack>
                </Box>
              ))}
              {runId ? (
                <Link href={dataAnchorHref} variant="body2" sx={{ mt: 0.5, display: "inline-block" }}>
                  {labels.linkScrollToDetailedData}
                </Link>
              ) : null}
            </Stack>
          </SectionCard>
        ) : null}

        {mergedExternalViews.length > 0 ? (
          <SectionCard title={labels.externalViewsSectionTitle}>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {mergedExternalViews.map((item, idx) => (
                <Button
                  key={`${item.href}-${idx}`}
                  component="a"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          </SectionCard>
        ) : null}
      </Stack>

      {pilotRef ? (
        <Accordion variant="outlined" disableGutters sx={{ borderRadius: 2, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
            <Typography fontWeight={600}>{labels.sectionPilotReference}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: pilotRef.kind === "object" ? 1 : 0 }}>
              {labels.pilotReferenceSummary}
            </Typography>
            {pilotRef.kind === "text" ? (
              <Typography variant="body2">{pilotRef.text}</Typography>
            ) : pilotRef.kind === "object" ? (
              <GenericStructuredValue value={pilotRef.value} runId={runId || undefined} />
            ) : null}
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}

const EVIDENCE_RESERVED = new Set([
  "metrics",
  "Metrics",
  "description",
  "Description",
  "threshold",
  "Threshold",
  "chart_html",
  "Chart_html",
]);

/**
 * Trusted HTML from your pipeline (e.g. Vega export). Renders after the metrics table when both exist.
 * Inline font styles in stored HTML are overridden (Inter, body2 — aligned with dense tables / secondary body text).
 * @param {{ html: string }} props
 */
function EvidenceChartHtml({ html }) {
  return (
    <Box
      component="div"
      className="evidence-chart-html"
      sx={(theme) => ({
        maxWidth: "100%",
        overflow: "auto",
        px: { xs: 1, sm: 2 },
        py: { xs: 2, sm: 2.5 },
        color: theme.palette.text.primary,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.body2.fontSize,
        lineHeight: theme.typography.body2.lineHeight,
        fontWeight: theme.typography.body2.fontWeight ?? 400,
        // Loosen common inline flex layouts from exported HTML (tight gap / margins)
        '& [style*="flex-direction:column"]': {
          gap: `${theme.spacing(3)} !important`,
          rowGap: `${theme.spacing(3)} !important`,
        },
        '& [style*="margin-bottom:10px"]': {
          marginBottom: `${theme.spacing(2)} !important`,
        },
        '& [style*="margin-bottom:8px"]': {
          marginBottom: `${theme.spacing(2)} !important`,
        },
        // Widen inline `gap:12px` clusters (labels + bars + badges)
        '& [style*="gap:12px"]': {
          gap: `${theme.spacing(2.5)} !important`,
        },
        '& [style*="gap: 12px"]': {
          gap: `${theme.spacing(2.5)} !important`,
        },
        // Beat inline Arial / pixel font sizes from exported HTML (body1 was visually larger than tables / cards)
        "& *": {
          fontFamily: `${theme.typography.fontFamily} !important`,
          fontSize: `${theme.typography.body2.fontSize} !important`,
          lineHeight: theme.typography.body2.lineHeight,
        },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          fontFamily: `${theme.typography.fontFamily} !important`,
          fontSize: `${theme.typography.subtitle2.fontSize} !important`,
          fontWeight: 600,
          color: theme.palette.text.primary,
          lineHeight: 1.35,
        },
        "& table": {
          borderCollapse: "collapse",
          width: "100%",
        },
        "& th, & td": {
          borderColor: theme.palette.divider,
        },
        "& th": {
          fontWeight: 600,
          bgcolor: "action.hover",
        },
        "& hr": {
          border: "none",
          borderTop: `1px solid ${theme.palette.divider}`,
          my: 2,
        },
        "& img": { maxWidth: "100%", height: "auto" },
      })}
      // eslint-disable-next-line react/no-danger -- intentional: server-generated chart markup stored on the run
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/**
 * `chart_html` may be a legacy single HTML string, or an array of `{ title, image_html }`.
 * @param {{ evidence: Record<string, unknown> }} props
 */
function EvidenceChartBlocks({ evidence }) {
  const raw = evidence.chart_html ?? evidence.Chart_html;
  if (raw == null) return null;

  if (typeof raw === "string") {
    const t = raw.trim();
    return t ? <EvidenceChartHtml html={t} /> : null;
  }

  if (!Array.isArray(raw) || raw.length === 0) return null;

  const blocks = raw
    .map((entry, idx) => {
      if (entry == null || typeof entry !== "object" || Array.isArray(entry)) return null;
      const img =
        typeof entry.image_html === "string"
          ? entry.image_html
          : typeof entry.Image_html === "string"
            ? entry.Image_html
            : "";
      const html = img.trim();
      if (!html) return null;
      const title =
        typeof entry.title === "string"
          ? entry.title.trim()
          : typeof entry.Title === "string"
            ? entry.Title.trim()
            : "";
      return (
        <Box key={idx}>
          {title ? (
            <Typography variant="subtitle2" component="h4" color="text.secondary" fontWeight={700} gutterBottom sx={{ letterSpacing: 0.04 }}>
              {title}
            </Typography>
          ) : null}
          <EvidenceChartHtml html={html} />
        </Box>
      );
    })
    .filter(Boolean);

  if (blocks.length === 0) return null;
  return (
    <Stack
      spacing={{ xs: 4, sm: 5 }}
      divider={<Divider flexItem sx={{ borderColor: "divider" }} />}
    >
      {blocks}
    </Stack>
  );
}

/** True if `chart_html` / `Chart_html` will render at least one chart block. */
function evidenceHasRenderableCharts(evidence) {
  const raw = evidence.chart_html ?? evidence.Chart_html;
  if (raw == null) return false;
  if (typeof raw === "string") return raw.trim().length > 0;
  if (!Array.isArray(raw) || raw.length === 0) return false;
  return raw.some((entry) => {
    if (entry == null || typeof entry !== "object" || Array.isArray(entry)) return false;
    const img =
      typeof entry.image_html === "string"
        ? entry.image_html
        : typeof entry.Image_html === "string"
          ? entry.Image_html
          : "";
    return img.trim().length > 0;
  });
}

/**
 * Optional intro copy + metrics table + optional chart HTML (no separate field captions for reserved keys).
 * `description` is free text; legacy numeric `threshold` still formats as a one-liner if no description.
 * @param {{ evidence: Record<string, unknown>; runId?: string }} props
 */
function EvidenceProofSection({ evidence, runId }) {
  const metrics = evidence.metrics ?? evidence.Metrics;
  const hasMetrics = Array.isArray(metrics) && metrics.length > 0;
  const other = Object.fromEntries(
    Object.entries(evidence).filter(([k, v]) => v != null && !EVIDENCE_RESERVED.has(k))
  );

  const rawDesc =
    typeof evidence.description === "string"
      ? evidence.description
      : typeof evidence.Description === "string"
        ? evidence.Description
        : "";
  const description = rawDesc.trim();
  const threshold = evidence.threshold ?? evidence.Threshold;

  const proofIntro =
    description ||
    (typeof threshold === "number" && Number.isFinite(threshold)
      ? `Applies a ${formatThreshold(threshold).toLowerCase()}.`
      : "");

  const chartBlock = <EvidenceChartBlocks evidence={evidence} />;
  const hasCharts = evidenceHasRenderableCharts(evidence);

  if (hasMetrics) {
    return (
      <Stack spacing={2}>
        {proofIntro ? (
          <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.75, whiteSpace: "pre-wrap" }}>
            {proofIntro}
          </Typography>
        ) : null}
        <GenericObjectTable rows={metrics} nameColumnHeader="Metrics" containerSx={{ my: 0 }} />
        {hasCharts ? (
          <>
            <Divider flexItem sx={{ borderColor: "divider" }} />
            {chartBlock}
          </>
        ) : null}
        {Object.keys(other).length > 0 ? <GenericStructuredValue value={other} runId={runId} /> : null}
      </Stack>
    );
  }

  if (proofIntro || hasCharts || Object.keys(other).length > 0) {
    return (
      <Stack spacing={2}>
        {proofIntro ? (
          <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.75, whiteSpace: "pre-wrap" }}>
            {proofIntro}
          </Typography>
        ) : null}
        {chartBlock}
        {Object.keys(other).length > 0 ? <GenericStructuredValue value={other} runId={runId} /> : null}
      </Stack>
    );
  }

  return <GenericStructuredValue value={evidence} runId={runId} />;
}

/**
 * @param {Record<string, unknown>} obj
 * @param {string} snake
 * @param {string} pascal
 */
function pickUncertaintyString(obj, snake, pascal) {
  const a = obj[snake];
  if (typeof a === "string" && a.trim()) return a;
  const b = obj[pascal];
  if (typeof b === "string" && b.trim()) return b;
  return "";
}

/**
 * Confidence copy: prose + validation table + reasoning bullets — no per-field captions.
 * @param {{ uncertainty: Record<string, unknown>; runId?: string }} props
 */
const DRIVERS_USED_KEYS = new Set([
  "description",
  "Description",
  "feature_importance",
  "Feature_importance",
  "feature_column_order",
  "Feature_column_order",
]);

/**
 * Description prose without a field title; other keys rendered generically (`feature_importance` omitted).
 * @param {{ drivers: Record<string, unknown>; runId?: string }} props
 */
function DriversSection({ drivers, runId }) {
  const d = drivers;
  const description = pickUncertaintyString(d, "description", "Description");

  const rest = Object.fromEntries(
    Object.entries(d).filter(([k, v]) => v != null && !DRIVERS_USED_KEYS.has(k))
  );

  const hasRest = Object.keys(rest).length > 0;

  if (!description && !hasRest) {
    return <GenericStructuredValue value={d} runId={runId} />;
  }

  return (
    <Stack spacing={2}>
      {description ? (
        <Typography variant="body1" color="text.primary" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.75 }}>
          {description}
        </Typography>
      ) : null}
      {hasRest ? <GenericStructuredValue value={rest} runId={runId} /> : null}
    </Stack>
  );
}

function UncertaintySection({ uncertainty, runId }) {
  const u = uncertainty;
  const confidence = pickUncertaintyString(u, "confidence", "Confidence");
  const modelAccuracy = pickUncertaintyString(u, "model_accuracy", "Model_accuracy");
  const metrics = u.validation_metrics ?? u.Validation_metrics;
  const reasoningRaw = u.reasoning ?? u.Reasoning;

  const hasMetrics =
    Array.isArray(metrics) &&
    metrics.length > 0 &&
    metrics.every((x) => x && typeof x === "object" && !Array.isArray(x));
  const hasReasoning =
    Array.isArray(reasoningRaw) && reasoningRaw.length > 0 && reasoningRaw.every((x) => typeof x === "string");

  if (!confidence && !modelAccuracy && !hasMetrics && !hasReasoning) {
    return <GenericStructuredValue value={u} runId={runId} />;
  }

  return (
    <Stack spacing={2}>
      {confidence ? (
        <Typography variant="body1" color="text.primary" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.75 }}>
          {confidence}
        </Typography>
      ) : null}
      {modelAccuracy ? (
        <Typography variant="body1" color="text.primary" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.75 }}>
          {modelAccuracy}
        </Typography>
      ) : null}
      {hasMetrics ? <GenericObjectTable rows={metrics} containerSx={{ my: 0 }} /> : null}
      {hasReasoning ? (
        <Box component="ul" sx={{ m: 0, pl: 2.5, my: 0 }}>
          {reasoningRaw.map((s, i) => (
            <li key={i}>
              <Typography variant="body1" component="span" sx={{ lineHeight: 1.75 }}>
                {s}
              </Typography>
            </li>
          ))}
        </Box>
      ) : null}
    </Stack>
  );
}

/** @param {{ title: string; children: import('react').ReactNode }} props */
function SectionCard({ title, children }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: "none", bgcolor: "background.paper", width: "100%" }}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography
          variant="h6"
          component="h2"
          color="primary"
          fontWeight={700}
          gutterBottom
          sx={{
            fontSize: { xs: "1.125rem", sm: "1.25rem" },
            letterSpacing: 0.02,
            mb: 2,
          }}
        >
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}
